<template>
  <div class="tw-mb-6">
    <h3 class="tw-m-0 tw-mb-2 tw-text-xs tw-uppercase">
      {{ I18n.t('file_attachments_sidebar.add_attachment.title') }}
    </h3>

    <MultiFileDirectUploader
      :on-file-added="handleFileAdded"
      :on-file-upload-progress="handleFileUploadProgress"
      :on-file-uploaded="handleFileUploaded"
      :on-all-files-uploaded="handleAllFilesUploaded"
      :before-upload="beforeUpload"
    >
      <div
        class="
          tw-px-6
          tw-py-8
          tw-flex
          tw-flex-col
          tw-justify-center
          tw-text-center
          tw-border
          tw-border-dashed
          tw-border-sf-snow-darker
          tw-bg-sf-snow-light
          tw-rounded-lg
          tw-cursor-pointer
        "
      >
        <i class="tw-mb-2 fa fa-file-upload tw-text-[23px]"></i>
        <div class="tw-mb-2 tw-leading-[1.4]">
          {{ I18n.t('file_attachments_sidebar.add_attachment.dropzone') }}
        </div>
        <div class="tw-mb-2 tw-flex tw-gap-2 tw-justify-center tw-items-center">
          <!-- Do not add a click handler. MultiFileDirectUploader receives this
               click and opens the file picker. -->
          <SfButtonDs variant="secondary" size="sm" icon-before="upload">
            {{
              I18n.t('file_attachments_sidebar.add_attachment.browse_button')
            }}
          </SfButtonDs>
          <SfButtonDs
            variant="secondary"
            size="sm"
            icon-before="folder-open"
            @click.stop="handleSelectExistingFile"
          >
            {{
              I18n.t(
                'file_attachments_sidebar.add_attachment.existing_document_button'
              )
            }}
          </SfButtonDs>
          <SfButtonDs
            variant="secondary"
            size="sm"
            icon-before="book-open"
            @click.stop="handleSelectExistingExport"
          >
            {{
              I18n.t(
                'file_attachments_sidebar.add_attachment.existing_export_button'
              )
            }}
          </SfButtonDs>
        </div>
        <div
          v-if="supportsExternalLinks"
          class="tw-flex tw-justify-center tw-items-center"
        >
          <span class="tw-inline-block tw-mr-0.5">{{
            I18n.t('file_attachments_sidebar.add_attachment.or')
          }}</span>
          <SfLink
            class="!tw-inline-block"
            @click.stop="handleAddNewExternalLink"
            >{{
              I18n.t('file_attachments_sidebar.add_attachment.link_external')
            }}</SfLink
          >
        </div>
      </div>
    </MultiFileDirectUploader>
  </div>
</template>

<script setup lang="ts">
import type { AttachingMode } from './constants'
import type { FileWithProgress } from '@app/shared/vue/components/direct-upload/types'

import { attachingMode } from './constants'
import { isFeatureSupported } from '@app/adapters/silverfin'
import MultiFileDirectUploader from '@app/shared/vue/components/direct-upload/multi-file-direct-uploader.vue'
import { SfButtonDs } from '@silverfin/ui-components'
import SfLink from '@app/shared/vue/components/sf-link.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'

const I18n = useI18n()

interface Props {
  beforeUpload?: ({
    file,
    uuid,
  }: {
    file: File
    uuid: string
  }) => Promise<boolean>
}

defineProps<Props>()

const emit =
  defineEmits<{
    'change-mode': [mode: AttachingMode]
    'on-file-added': [file: FileWithProgress, uuid: string]
    'on-file-upload-progress': [
      uuid: string,
      file: FileWithProgress,
      progress: string
    ]
    'on-file-uploaded': [file: FileWithProgress, uuid: string, length: number]
    'on-all-files-uploaded': [status: boolean]
  }>()

const supportsExternalLinks = isFeatureSupported('supports_external_links')

function handleFileAdded(file: FileWithProgress, uuid: string) {
  emit('on-file-added', file, uuid)
}

function handleFileUploadProgress(
  uuid: string,
  file: FileWithProgress,
  progress: string
) {
  emit('on-file-upload-progress', uuid, file, progress)
}

function handleFileUploaded(
  file: FileWithProgress,
  uuid: string,
  length: number
) {
  emit('on-file-uploaded', file, uuid, length)
}

function handleAllFilesUploaded(status: boolean) {
  emit('on-all-files-uploaded', status)
}

function handleSelectExistingFile() {
  emit('change-mode', attachingMode.EXISTING_FILES)
}

function handleSelectExistingExport() {
  emit('change-mode', attachingMode.EXISTING_EXPORTS)
}

function handleAddNewExternalLink() {
  emit('change-mode', attachingMode.EXTERNAL_LINK)
}
</script>
